import * as React from 'react';
import { Instagram, Twitter, Youtube } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PlatformIconProps {
  /** Platform code. Case-insensitive: matches TIKTOK / Tiktok / tiktok etc. */
  platform: string | null | undefined;
  /** Square size in pixels. Defaults to 12 to match our chip + cell line-heights. */
  size?: number;
  className?: string;
}

/**
 * Inline TikTok glyph. Lucide doesn't ship a TikTok icon (brand-trademark
 * reasons), so we carry the same handcrafted SVG used in `prod-clipmore-user`.
 * Fills from `currentColor` so it inherits whatever text color the parent
 * sets (channel tag tone, ghost button hover, etc.).
 */
function TikTokGlyph({ size = 12, className }: { size?: number; className?: string }): JSX.Element {
  return (
    <svg
      fill="currentColor"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      className={className}
      aria-hidden
    >
      <path d="M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z" />
    </svg>
  );
}

/**
 * Inline Discord glyph (Clyde mascot). Lucide doesn't ship a Discord
 * brand icon either; this is the canonical solid logomark.
 */
function DiscordGlyph({ size = 12, className }: { size?: number; className?: string }): JSX.Element {
  return (
    <svg
      fill="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      className={className}
      aria-hidden
    >
      <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09-.01-.02-.04-.03-.07-.03-1.5.26-2.93.71-4.27 1.33-.01 0-.02.01-.03.02-2.72 4.07-3.47 8.03-3.1 11.95 0 .02.01.04.03.05 1.8 1.32 3.53 2.12 5.24 2.65.03.01.06 0 .07-.02.4-.55.76-1.13 1.07-1.74.02-.04 0-.08-.04-.09-.57-.22-1.11-.48-1.64-.78-.04-.02-.04-.08-.01-.11.11-.08.22-.17.33-.25.02-.02.05-.02.07-.01 3.44 1.57 7.15 1.57 10.55 0 .02-.01.05-.01.07.01.11.09.22.17.33.26.04.03.04.09-.01.11-.52.31-1.07.56-1.64.78-.04.01-.05.06-.04.09.32.61.68 1.19 1.07 1.74.03.01.06.02.09.01 1.72-.53 3.45-1.33 5.25-2.65.02-.01.03-.03.03-.05.44-4.53-.73-8.46-3.1-11.95-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12 0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12 0 1.17-.83 2.12-1.89 2.12z" />
    </svg>
  );
}

/**
 * Renders the brand glyph for a social-media platform. Unknown / null
 * platforms render nothing (returns `null`), so this is safe to drop into
 * row anchors and tag chips without a guard.
 *
 * For lucide-supplied platforms (Instagram / YouTube / Twitter), we lean
 * on stroke icons sized via `size`. For TikTok and Discord — which lucide
 * doesn't ship — we carry filled SVGs that inherit `currentColor`.
 */
export function PlatformIcon({
  platform,
  size = 12,
  className,
}: PlatformIconProps): JSX.Element | null {
  if (!platform) return null;
  const cls = cn('shrink-0', className);
  switch (platform.toUpperCase()) {
    case 'TIKTOK':
      return <TikTokGlyph size={size} className={cls} />;
    case 'INSTAGRAM':
      return <Instagram size={size} className={cls} aria-hidden />;
    case 'YOUTUBE':
      return <Youtube size={size} className={cls} aria-hidden />;
    case 'X':
    case 'TWITTER':
      return <Twitter size={size} className={cls} aria-hidden />;
    case 'DISCORD':
      return <DiscordGlyph size={size} className={cls} />;
    default:
      return null;
  }
}
